// src/components/founderDashboard/EngagementCard.tsx
// Who opens the app, how often and how recently: one row per member who has
// opened it since DailyActive started recording, from /founder-portal/engagement/.
// Fixed windows (all tracked days, last 30 days, last 8 weeks), so the page's
// range picker does not apply here. Renders two cards from the one fetch: the
// regularity vs session length bubble chart, then the member table.
import React, { useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer, BarChart, Bar, LabelList, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip,
    ScatterChart, Scatter, ReferenceLine,
} from "recharts";
import { api } from "./apiClient";
import {
    C, FONT, numStyle, fmt, shortDate, parseDate, Card, Empty, axisX, axisY, gridProps, tipBar,
    th, td, tdNum, TableWrap, TipBox, Legend,
} from "./hq";

type Row = Record<string, any>;
type SortKey = "name" | "membership" | "last_seen" | "days_opened" | "pct_days" | "days_30d" | "sessions_30d" | "avg_session_min" | "completions_30d";

const PREVIEW = 25;
// Under this many days since joining, a % of days is too noisy to rank on.
const MIN_TRACKED = 14;
const MEMBER_STATUSES = new Set(["Premium", "Trial", "Early", "Grandfathered", "Free week"]);
// Quadrant lines on the bubble chart: about twice a week, and a five-minute visit.
const REGULAR_DAYS = 8;
const LONG_MIN = 5;
const BUBBLE_GROUPS = [
    { key: "member", label: "Member", color: C.slate },
    { key: "free", label: "Free, lapsed or former", color: C.neutral },
    { key: "team", label: "Team", color: C.rust },
] as const;

const lastOpened = (iso: string) => {
    const d = parseDate(iso);
    if (!d) return { label: "-", color: C.faint };
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    const label = days <= 0 ? "today" : days === 1 ? "yesterday" : `${days}d ago`;
    return { label, color: days <= 1 ? C.sageD : days >= 14 ? C.alert : C.ink };
};

// One square per week, deepening with the number of days opened that week.
const weekCell = (n: number | null) =>
    n === null ? "transparent" : n === 0 ? C.inset : `rgba(97,107,79,${(0.18 + (n / 7) * 0.82).toFixed(2)})`;

export default function EngagementCard() {
    const [data, setData] = useState<Row | null>(null);
    const [err, setErr] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>("days_30d");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        api.get("/founder-portal/engagement/")
            .then(res => setData(res.data))
            .catch(e => setErr(e?.response?.data?.detail || e?.message || "Failed to load"));
    }, []);

    const rows: Row[] = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1;
        return [...(data?.members || [])].sort((a: Row, b: Row) => {
            const av = a[sortKey], bv = b[sortKey];
            const primary = typeof av === "string" || typeof bv === "string"
                ? String(av ?? "").localeCompare(String(bv ?? ""))
                : (av ?? -Infinity) - (bv ?? -Infinity);
            return primary * dir || (b.days_opened - a.days_opened) || (b.sessions_30d - a.sessions_30d);
        });
    }, [data, sortKey, sortDir]);

    if (err) return <Card title="Member engagement"><Empty>{err}</Empty></Card>;
    if (!data) return <Card title="Member engagement"><div style={{ color: C.muted, fontFamily: FONT, fontSize: 13 }}>Loading engagement…</div></Card>;

    const members: Row[] = data.members || [];
    const regulars = members.filter(m => m.days_30d >= 15).length;

    const bubbles = members
        .filter(m => m.avg_session_min !== null && m.avg_session_min !== undefined)
        .map((m): Row => ({
            ...m,
            group: m.team ? "team" : MEMBER_STATUSES.has(m.membership) ? "member" : "free",
            first: String(m.name).split(" ")[0],
        }));
    const counted = bubbles.filter(b => !b.team);
    const quadrants = [
        ["Regular + long", counted.filter(b => b.days_30d >= REGULAR_DAYS && b.avg_session_min >= LONG_MIN).length],
        ["Regular + short", counted.filter(b => b.days_30d >= REGULAR_DAYS && b.avg_session_min < LONG_MIN).length],
        ["Occasional + long", counted.filter(b => b.days_30d < REGULAR_DAYS && b.avg_session_min >= LONG_MIN).length],
        ["Occasional + short", counted.filter(b => b.days_30d < REGULAR_DAYS && b.avg_session_min < LONG_MIN).length],
    ] as const;
    // Headroom above the longest average so the biggest bubbles and their labels are not clipped.
    const yMax = Math.max(LONG_MIN * 2, Math.ceil((Math.max(0, ...bubbles.map(b => b.avg_session_min)) + 3) / 5) * 5);
    const yTicks = Array.from({ length: yMax / 5 + 1 }, (_, i) => i * 5);

    const shown = showAll ? rows : rows.slice(0, PREVIEW);
    const weekStarts: string[] = data.week_starts || [];

    const head = (label: string, key: SortKey, align: "left" | "right" = "right") => (
        <th onClick={() => {
            if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
            else { setSortKey(key); setSortDir(key === "name" || key === "membership" ? "asc" : "desc"); }
        }} style={{ ...th(align, sortKey === key), cursor: "pointer", userSelect: "none" }}>
            {label}{sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
        </th>
    );

    return (<>
        <Card title="Regularity vs session length, last 30 days" right={
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {quadrants.map(([label, n]) => (
                    <div key={label} style={{ display: "flex", gap: 6, alignItems: "baseline", background: C.panel, padding: "6px 12px", borderRadius: 10 }}>
                        <span style={{ ...numStyle(18), lineHeight: 1 }}>{fmt(n)}</span>
                        <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: "rgba(22,25,37,.6)" }}>{label}</span>
                    </div>
                ))}
            </div>
        } gap={12}>
            {bubbles.length ? (<>
                <ResponsiveContainer width="100%" height={420}>
                    <ScatterChart margin={{ top: 16, right: 24, bottom: 18, left: 4 }}>
                        <CartesianGrid stroke={C.grid} />
                        <XAxis {...axisX} dataKey="days_30d" name="days opened" type="number" domain={[0, 31]} ticks={[0, 5, 10, 15, 20, 25, 30]}
                            label={{ value: "Days opened, last 30", position: "insideBottom", offset: -12, style: { fontFamily: FONT, fontSize: 11, fill: C.tick } }} />
                        <YAxis {...axisY} dataKey="avg_session_min" name="avg session" type="number" domain={[0, yMax]} ticks={yTicks} width={44}
                            label={{ value: "Avg session (min)", angle: -90, position: "insideLeft", offset: 12, style: { fontFamily: FONT, fontSize: 11, fill: C.tick, textAnchor: "middle" } }} />
                        <ZAxis dataKey="sessions_30d" range={[30, 900]} name="sessions" />
                        <ReferenceLine x={REGULAR_DAYS - 0.5} stroke={C.base} strokeDasharray="5 4" />
                        <ReferenceLine y={LONG_MIN} stroke={C.base} strokeDasharray="5 4" />
                        <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => payload?.length ? (
                            <TipBox title={`${payload[0].payload.name} (${payload[0].payload.membership})`}>
                                {fmt(payload[0].payload.days_30d)} days · {fmt(payload[0].payload.avg_session_min, 1)} min avg · {fmt(payload[0].payload.sessions_30d)} sessions
                            </TipBox>) : null} />
                        {BUBBLE_GROUPS.map(g => (
                            <Scatter key={g.key} name={g.label} data={bubbles.filter(b => b.group === g.key)}
                                fill={g.color} fillOpacity={g.key === "free" ? 0.7 : 0.75} stroke="#FFF" strokeWidth={1}>
                                {g.key === "team" && <LabelList dataKey="first" position="top" style={{ fontFamily: FONT, fontWeight: 600, fontSize: 11, fill: C.ink }} />}
                            </Scatter>
                        ))}
                    </ScatterChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", gap: "6px 16px", flexWrap: "wrap", alignItems: "center" }}>
                    <Legend items={[
                        ...BUBBLE_GROUPS.map(g => ({ label: g.label, color: g.color, kind: "dot" as const })),
                        { label: `${REGULAR_DAYS} days / ${LONG_MIN} min`, color: C.base, kind: "dash" as const },
                    ]} />
                    <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: C.muted }}>Bubble size = sessions</span>
                </div>
            </>) : <Empty>No sessions recorded in the last 30 days.</Empty>}
        </Card>

        <Card title="Member engagement" right={
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {([[data.opened_30d, "opened in the last 30 days"], [regulars, "on 15+ of those days"]] as const).map(([n, label]) => (
                    <div key={label} style={{ display: "flex", gap: 6, alignItems: "baseline", background: C.panel, padding: "6px 12px", borderRadius: 10 }}>
                        <span style={{ ...numStyle(18), lineHeight: 1 }}>{fmt(n)}</span>
                        <span style={{ fontFamily: FONT, fontWeight: 500, fontSize: 12, color: "rgba(22,25,37,.6)" }}>{label}</span>
                    </div>
                ))}
            </div>
        } gap={16}>
            {members.length ? (<>
                <div>
                    <div style={{ fontFamily: FONT, fontWeight: 600, fontSize: 12, color: C.muted, marginBottom: 6 }}>Members by days opened, last 30 days</div>
                    <ResponsiveContainer width="100%" height={150}>
                        <BarChart data={data.buckets} margin={{ top: 18, right: 4, bottom: 0, left: -10 }} barCategoryGap="24%">
                            <CartesianGrid {...gridProps} />
                            <XAxis {...axisX} dataKey="label" tickFormatter={(v: string) => (v === "1" ? "1 day" : `${v} days`)} />
                            <YAxis {...axisY} width={30} allowDecimals={false} />
                            <Tooltip {...tipBar} labelFormatter={(v: any) => (v === "1" ? "Opened on 1 day" : `Opened on ${v} days`)} />
                            <Bar dataKey="members" name="Members" fill={C.sage} radius={[4, 4, 0, 0]}>
                                <LabelList dataKey="members" position="top" style={{ ...numStyle(13), fill: C.ink }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <TableWrap minWidth={1100}>
                    <thead>
                        <tr>
                            {head("Member", "name", "left")}
                            {head("Membership", "membership", "left")}
                            {head("Last opened", "last_seen")}
                            {head("Days opened", "days_opened")}
                            {head("% of days", "pct_days")}
                            {head("Last 30d", "days_30d")}
                            {head("Sessions 30d", "sessions_30d")}
                            {head("Avg session", "avg_session_min")}
                            {head("Workouts 30d", "completions_30d")}
                            <th style={th("left")}>Last 8 weeks</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((m: Row) => {
                            const lo = lastOpened(m.last_seen);
                            const isNew = m.tracked_days < MIN_TRACKED;
                            const member = MEMBER_STATUSES.has(m.membership);
                            return (
                                <tr key={m.id} className="hq-row">
                                    <td style={{ ...td(), fontWeight: 600, whiteSpace: "nowrap" }}>{m.name}</td>
                                    <td style={td()}>
                                        <span style={{
                                            fontFamily: FONT, fontWeight: 600, fontSize: 11, padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap",
                                            background: member ? "rgba(144,155,123,.18)" : C.panel, color: member ? C.sageD : "rgba(22,25,37,.6)",
                                        }}>{m.membership}</span>
                                    </td>
                                    <td style={{ ...td("right"), whiteSpace: "nowrap" }}>
                                        <span style={{ fontWeight: 500, color: lo.color }}>{lo.label}</span>
                                        {m.streak >= 3 && <span style={{ fontFamily: FONT, fontSize: 11, color: C.muted }}> · {m.streak}-day streak</span>}
                                    </td>
                                    <td style={tdNum()} title={`of ${m.tracked_days} tracked days, first opened ${shortDate(m.first_seen)}`}>{fmt(m.days_opened)}</td>
                                    <td style={td("right")} title={isNew ? `Joined ${shortDate(m.joined)}: only ${m.tracked_days} days to judge` : `${m.days_opened} of ${m.tracked_days} tracked days`}>
                                        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end" }}>
                                            <div style={{ width: 60, height: 6, borderRadius: 6, background: C.track }}>
                                                <div style={{ height: 6, borderRadius: 6, width: `${Math.min(100, m.pct_days || 0)}%`, background: isNew ? C.sageL : C.sage }} />
                                            </div>
                                            <span style={{ ...numStyle(15), width: 40, textAlign: "right", color: isNew ? C.faint : C.ink }}>{fmt(m.pct_days)}%</span>
                                        </div>
                                    </td>
                                    <td style={tdNum()}>{fmt(m.days_30d)}</td>
                                    <td style={tdNum(500)}>{fmt(m.sessions_30d)}</td>
                                    <td style={tdNum(500, m.avg_session_min === null ? C.faint : C.ink)}>
                                        {m.avg_session_min === null ? "-" : `${fmt(m.avg_session_min, 1)} min`}
                                    </td>
                                    <td style={tdNum(500, m.completions_30d ? C.ink : C.faint)}>{fmt(m.completions_30d)}</td>
                                    <td style={td()}>
                                        <div style={{ display: "flex", gap: 3 }}>
                                            {(m.weeks || []).map((n: number | null, i: number) => (
                                                <span key={i} title={`w/c ${shortDate(weekStarts[i])}: ${n === null ? "not a member yet" : `${n} day${n === 1 ? "" : "s"}`}`}
                                                    style={{ width: 14, height: 14, borderRadius: 4, background: weekCell(n), border: n === null ? `1px dashed ${C.hair}` : "none" }} />
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </TableWrap>
                {rows.length > PREVIEW && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, fontFamily: FONT, fontSize: 12, color: C.muted }}>
                        <span>Showing {fmt(shown.length)} of {fmt(rows.length)} members who have opened the app since {shortDate(data.tracking_since)}</span>
                        <button onClick={() => setShowAll(s => !s)} style={{
                            fontFamily: FONT, fontWeight: 700, fontSize: 13, color: C.ink, background: "#FFF",
                            border: `1.5px solid ${C.ink}`, borderRadius: 999, padding: "7px 16px", cursor: "pointer",
                        }}>{showAll ? "Show fewer" : "Show all"}</button>
                    </div>
                )}
            </>) : <Empty>Nobody has opened the app since recording started.</Empty>}
        </Card>
    </>);
}
